import './index.css'

import { LuCircle } from '@react-icons/all-files/lu/LuCircle'
import { LuSparkles } from '@react-icons/all-files/lu/LuSparkles'
import cn from 'clsx'
import React, { useCallback } from 'react'

import type { ColorIndicator } from '@disco/utils/color-indicators'
import { COLORS } from '@disco/utils/color-indicators'

import { Badge } from 'components/ds'
import Icon from 'components/ui/icon'

export type Props = {
  tag: string
  tagColor?: ColorIndicator | null | undefined
  /** Marks a tag applied by auto-tagging — renders the AI sparkles after
   * the color dot. */
  ai?: boolean
  /** Radix Badge variant. Defaults to the existing soft-gray fill so
   * every current call site keeps its look; pass "surface" for the
   * white-with-a-subtle-border treatment used in the track tags panel. */
  variant?: 'soft' | 'surface'
  onTagClick?: (tag: string) => void
  onRemoveClick?: () => void
  icon?: React.ReactNode
  checked?: boolean
  disabled?: boolean
  action?: React.ReactNode
}

/**
 * A tag component that displays a label with optional color indicator, icon, and remove functionality
 * @param tag - The text content of the tag
 * @param tagColor - Optional color indicator for the tag
 * @param onTagClick - Optional callback called when the tag is clicked
 * @param onRemoveClick - Optional callback called when the remove button is clicked
 * @param icon - Optional icon to display at the start of the tag
 * @param checked - Whether to show a check mark on the tag
 * @param disabled - Whether the tag is disabled
 * @returns A tag component with optional interactive features
 */
const Tag = (props: Props): React.ReactElement => {
  const handleClick = useCallback(() => {
    if (props.onTagClick) {
      props.onTagClick(props.tag)
    }
  }, [props.tag, props.onTagClick])

  const handleRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (props.onRemoveClick && !props.disabled) {
        props.onRemoveClick()
      }
    },
    [props.onRemoveClick, props.disabled],
  )

  return (
    <Badge
      size="2"
      variant={props.variant ?? 'soft'}
      color="gray"
      className="Tag-root"
    >
      {props.icon && <div className="Tag-icon">{props.icon}</div>}
      <span
        className={cn('Tag-name', {
          'Tag-name--link': props.onTagClick,
        })}
        onClick={handleClick}
      >
        {props.ai && <LuSparkles size={12} className="Tag-ai" />}
        {props.tag}
        {props.tagColor && (
          <LuCircle
            size={8}
            fill={COLORS[props.tagColor]}
            stroke="none"
            className="Tag-color"
          />
        )}
      </span>
      {props.checked && (
        <span className="Tag-container">
          <Icon type="check" width="16" height="16" />
        </span>
      )}
      {props.onRemoveClick && (
        <span
          className={cn('Tag-remove', {
            'Tag-remove--disabled': props.disabled,
          })}
          onClick={handleRemoveClick}
        >
          <Icon type="close" width="16" height="16" />
        </span>
      )}
      {props.action && <span className="Tag-action">{props.action}</span>}
    </Badge>
  )
}

export default Tag
